import React, { useState, useEffect } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, History, Info } from 'lucide-react';
import { getAsbabunNuzulChapter, getAsbabunNuzulAyah } from '../services/tafsirService';

const AsbabunNuzulCard = ({ surahNumber, ayahNumber }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAsbabunNuzul();
  }, [surahNumber, ayahNumber]);

  const fetchAsbabunNuzul = async () => {
    if (!surahNumber) return;

    setLoading(true);
    setError(null);
    try {
      // Try to get ayah-level asbabun nuzul first
      let result = await getAsbabunNuzulAyah(surahNumber, ayahNumber);

      // If no ayah-level results, try chapter-level
      if (!result.asbabun_nuzul || result.asbabun_nuzul.length === 0) {
        result = await getAsbabunNuzulChapter(surahNumber);
      }

      setData(result);
    } catch (err) {
      console.error('Error fetching asbabun nuzul:', err);
      setError('Gagal memuat data asbabun nuzul');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Memuat asbabun nuzul...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data || !data.asbabun_nuzul || data.asbabun_nuzul.length === 0) {
    return (
      <Alert>
        <History className="h-4 w-4" />
        <AlertDescription>
          Maaf, informasi asbabun nuzul belum tersedia untuk ayat ini.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {data.asbabun_nuzul.map((asbab, index) => (
        <div key={asbab.asbab_id || index} className="space-y-4">
          {/* Arabic Text (if available) */}
          {asbab.reason_arabic && (
            <div className="text-right bg-secondary/30 rounded-lg p-4">
              <p className="text-xl leading-loose font-arabic" dir="rtl">
                {asbab.reason_arabic}
              </p>
            </div>
          )}

          {/* Indonesian Text */}
          <ScrollArea className="h-[300px] w-full rounded-md border">
            <div className="p-4">
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <History className="h-4 w-4 text-primary" />
                  Sebab Turunnya Ayat
                </h4>

                {asbab.ayah_number_start && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {asbab.ayah_number_start === asbab.ayah_number_end
                        ? `Ayat ${asbab.ayah_number_start}`
                        : `Ayat ${asbab.ayah_number_start} - ${asbab.ayah_number_end}`}
                    </Badge>
                  </div>
                )}

                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {asbab.reason_indonesian}
                </p>

                {/* Reference */}
                {asbab.reference && (
                  <div className="mt-4 pt-3 border-t">
                    <p className="text-xs text-muted-foreground">
                      <strong>Sumber:</strong> {asbab.reference}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          {/* Separator between multiple asbabun nuzul entries */}
          {index < data.asbabun_nuzul.length - 1 && (
            <div className="border-t pt-4">
              <p className="text-xs text-muted-foreground text-center">
                ──── Lanjut ke asbabun nuzul lainnya ────
              </p>
            </div>
          )}
        </div>
      ))}

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          <strong>Asbabun Nuzul</strong> adalah sebab atau latar belakang turunnya suatu ayat Al-Qur'an.
          Memahami asbabun nuzul membantu kita memahami konteks dan makna ayat yang lebih mendalam.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default AsbabunNuzulCard;
